import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Clock, Zap, ListChecks, Save, X } from "lucide-react";
import api, { apiErr } from "../lib/api";
import Btn from "../components/Btn";

const EXAM_COLOR = { CPNS: "var(--su-primary)", SNBT: "var(--su-success)" };

const EMPTY = {
  package_id: "", exam: "CPNS", mode: "tryout", title: "", subtitle: "", duration_minutes: 20, energy_cost: 10, token_cost: 0,
  scoring: "cpns", sections: [], passing_grade: null, order: 100, active: true,
};

export default function AdminPackages() {
  const [packages, setPackages] = useState([]);
  const [cats, setCats] = useState({});
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => Promise.all([
    api.get("/admin/packages").then(({ data }) => setPackages(data.packages)),
    api.get("/admin/categories").then(({ data }) => setCats(data.categories)),
  ]).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const remove = async (p) => {
    if (!window.confirm(`Hapus tryout "${p.title}"?`)) return;
    try { await api.delete(`/admin/packages/${p.package_id}`); toast.success("Tryout dihapus"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (loading) return <div className="min-h-[30vh] grid place-items-center"><div className="w-10 h-10 rounded-full border-4 border-su-border border-t-su-primary animate-spin" /></div>;
  if (editing) return <PackageForm initial={editing === "new" ? EMPTY : editing} isNew={editing === "new"} cats={cats} onDone={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />;

  return (
    <div className="space-y-5" data-testid="admin-packages">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading font-black text-lg">Kelola Jenis Tryout</h2>
          <p className="text-su-muted font-medium text-sm">Tambah, edit, atau hapus paket simulasi CBT.</p>
        </div>
        <Btn data-testid="package-new" onClick={() => setEditing("new")}><Plus className="w-4 h-4" /> Tambah Tryout</Btn>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {packages.map((p) => {
          const color = EXAM_COLOR[p.exam] || "var(--su-primary)";
          const total = (p.sections || []).reduce((a, s) => a + (s.count || 0), 0);
          return (
            <div key={p.package_id} className="card-chunky p-5" data-testid={`pkg-card-${p.package_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-heading font-black uppercase px-2 py-0.5 rounded-full" style={{ color, background: `${color}22` }}>{p.exam}</span>
                    <span className="text-[10px] font-heading font-black uppercase px-2 py-0.5 rounded-full bg-su-secondary/15 text-su-secondaryShadow">{p.mode === "drill" ? "Drill Kilat" : "Tryout"}</span>
                    {!p.active && <span className="text-[10px] font-heading font-black bg-su-track text-su-muted px-2 py-0.5 rounded-full">NONAKTIF</span>}
                    {p.mega_only && <span className="text-[10px] font-heading font-black uppercase px-2 py-0.5 rounded-full bg-su-danger/15 text-su-danger">Khusus Akbar</span>}
                  </div>
                  <h4 className="font-heading font-black text-lg mt-1">{p.title}</h4>
                  <p className="text-xs text-su-muted">{p.subtitle}</p>
                  <div className="flex items-center gap-4 text-xs font-heading font-bold text-su-muted mt-2">
                    <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{p.duration_minutes}m</span>
                    <span className="flex items-center gap-1 text-su-energy"><Zap className="w-3.5 h-3.5 fill-current" />{p.energy_cost}</span>
                    <span className="flex items-center gap-1"><ListChecks className="w-3.5 h-3.5" />{total} soal</span>
                    <span className="uppercase">{p.scoring}</span>
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button data-testid={`pkg-edit-${p.package_id}`} onClick={() => setEditing(p)} className="w-9 h-9 grid place-items-center rounded-xl border-2 border-su-border hover:border-su-primary transition-colors"><Pencil className="w-4 h-4" /></button>
                  <button data-testid={`pkg-delete-${p.package_id}`} onClick={() => remove(p)} className="w-9 h-9 grid place-items-center rounded-xl border-2 border-su-border hover:border-su-danger text-su-danger transition-colors"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PackageForm({ initial, isNew, cats, onDone, onCancel }) {
  const [f, setF] = useState(() => ({
    mode: "tryout", ...initial,
    sections: (initial.sections || []).map((s) => ({ ...s, subcategory: s.subcategory || "", pg: initial.passing_grade?.[s.category] ?? "" })),
  }));
  const [tree, setTree] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/questions/tree").then(({ data }) => setTree(data.tree || {})).catch(() => {}); }, []);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const catList = Object.keys(cats[f.exam] || {});
  const subsOf = (cat) => Object.keys(tree[f.exam]?.categories?.[cat]?.subcategories || {});
  const isDrill = f.mode === "drill";

  const setSection = (i, k, v) => setF((s) => { const a = [...s.sections]; a[i] = { ...a[i], [k]: v }; if (k === "category") a[i].subcategory = ""; return { ...s, sections: a }; });
  const addSection = () => setF((s) => ({ ...s, sections: [...s.sections, { category: catList[0] || "", subcategory: "", count: 5, label: "", pg: "" }] }));
  const rmSection = (i) => setF((s) => ({ ...s, sections: s.sections.filter((_, j) => j !== i) }));

  const changeMode = (m) => setF((s) => ({ ...s, mode: m, sections: m === "drill" ? [{ category: catList[0] || "", subcategory: "", count: 10, label: "", pg: "" }] : [] }));

  const save = async () => {
    if (!f.title.trim()) { toast.error("Judul wajib diisi"); return; }
    if (f.sections.length === 0) { toast.error("Tambahkan minimal satu bagian"); return; }
    const passing_grade = {};
    if (!isDrill) f.sections.forEach((s) => { if (f.scoring === "cpns" && s.pg !== "" && s.pg != null) passing_grade[s.category] = Number(s.pg); });
    const payload = {
      ...f, mode: f.mode,
      duration_minutes: Number(f.duration_minutes) || 1,
      energy_cost: Number(f.energy_cost) || 0,
      token_cost: Number(f.token_cost) || 0,
      order: Number(f.order) || 100,
      sections: f.sections.map((s) => ({ category: s.category, subcategory: s.subcategory || "", count: Number(s.count) || 1, label: s.label || s.subcategory || s.category })),
      passing_grade: Object.keys(passing_grade).length ? passing_grade : null,
    };
    setBusy(true);
    try {
      if (isNew) await api.post("/admin/packages", payload);
      else await api.put(`/admin/packages/${initial.package_id}`, payload);
      toast.success(isNew ? "Berhasil ditambahkan!" : "Berhasil diperbarui!");
      onDone();
    } catch (e) { toast.error(apiErr(e)); setBusy(false); }
  };

  const inputCls = "w-full bg-su-bg border-2 border-su-border rounded-xl px-4 py-2.5 font-medium focus:border-su-primary outline-none transition-colors";
  const lbl = "text-xs font-heading font-black uppercase text-su-muted mb-1.5 block";

  return (
    <div className="card-chunky p-6 max-w-2xl" data-testid="package-form">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-heading font-black text-lg">{isNew ? "Tambah Tryout" : "Edit Tryout"}</h2>
        <button onClick={onCancel} data-testid="pkg-cancel" className="w-9 h-9 grid place-items-center rounded-xl border-2 border-su-border"><X className="w-4 h-4" /></button>
      </div>
      <div className="space-y-4">
        <div>
          <label className={lbl}>Jenis</label>
          <div className="grid grid-cols-2 gap-2" data-testid="pkgf-mode">
            {[["tryout", "Tryout (CBT)"], ["drill", "Drill Kilat"]].map(([m, t]) => (
              <button key={m} data-testid={`pkgf-mode-${m}`} onClick={() => changeMode(m)}
                className={`py-2.5 rounded-xl font-heading font-black text-sm border-2 transition-all ${f.mode === m ? "bg-su-primary text-white border-su-primaryShadow" : "bg-su-surface text-su-muted border-su-border"}`}>{t}</button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div><label className={lbl}>Jenis Ujian</label>
            <select data-testid="pkgf-exam" value={f.exam} onChange={(e) => { const ex = e.target.value; const cl = Object.keys(cats[ex] || {}); setF((s) => ({ ...s, exam: ex, sections: s.mode === "drill" ? [{ category: cl[0] || "", subcategory: "", count: 10, label: "", pg: "" }] : [] })); }} className={inputCls}>
              {Object.keys(cats).length ? Object.keys(cats).map((ex) => <option key={ex} value={ex}>{ex}</option>) : <><option>CPNS</option><option>SNBT</option></>}
            </select>
          </div>
          <div><label className={lbl}>Sistem Skor</label>
            <select data-testid="pkgf-scoring" value={f.scoring} onChange={(e) => set("scoring", e.target.value)} className={inputCls}>
              <option value="cpns">CPNS (Passing Grade)</option>
              <option value="irt">IRT (Skala 1000)</option>
            </select>
          </div>
        </div>
        <div><label className={lbl}>Judul</label><input data-testid="pkgf-title" value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="Simulasi SKD CPNS" className={inputCls} /></div>
        <div><label className={lbl}>Subjudul</label><input data-testid="pkgf-subtitle" value={f.subtitle} onChange={(e) => set("subtitle", e.target.value)} placeholder="TWK · TIU · TKP" className={inputCls} /></div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div><label className={lbl}>Durasi (mnt)</label><input data-testid="pkgf-duration" type="number" value={f.duration_minutes} onChange={(e) => set("duration_minutes", e.target.value)} className={inputCls} /></div>
          <div><label className={lbl}>Biaya Energi</label><input data-testid="pkgf-energy" type="number" value={f.energy_cost} onChange={(e) => set("energy_cost", e.target.value)} className={inputCls} /></div>
          <div><label className={lbl}>Biaya Token</label><input data-testid="pkgf-token" type="number" value={f.token_cost} onChange={(e) => set("token_cost", e.target.value)} className={inputCls} /></div>
          <div><label className={lbl}>Urutan</label><input data-testid="pkgf-order" type="number" value={f.order} onChange={(e) => set("order", e.target.value)} className={inputCls} /></div>
        </div>

        <div>
          <label className={lbl}>{isDrill ? "Kategori & subtopik soal" : "Bagian / Section (kategori soal & jumlah)"}</label>
          <div className="space-y-2">
            {f.sections.map((s, i) => (
              <div key={i} className="flex gap-2 items-start" data-testid={`pkg-section-${i}`}>
                <select data-testid={`pkg-sec-cat-${i}`} value={s.category} onChange={(e) => setSection(i, "category", e.target.value)} className={inputCls + " flex-1"}>
                  {catList.map((c) => <option key={c} value={c}>{`${c} (${cats[f.exam][c]} soal)`}</option>)}
                </select>
                {isDrill && (
                  <select data-testid={`pkg-sec-sub-${i}`} value={s.subcategory} onChange={(e) => setSection(i, "subcategory", e.target.value)} className={inputCls + " flex-1"}>
                    <option value="">Semua subtopik</option>
                    {subsOf(s.category).map((sub) => <option key={sub} value={sub}>{sub}</option>)}
                  </select>
                )}
                <input data-testid={`pkg-sec-count-${i}`} type="number" title="Jumlah soal" value={s.count} onChange={(e) => setSection(i, "count", e.target.value)} className={inputCls + " w-20"} />
                {!isDrill && f.scoring === "cpns" && <input data-testid={`pkg-sec-pg-${i}`} type="number" title="Passing grade (opsional)" placeholder="PG" value={s.pg} onChange={(e) => setSection(i, "pg", e.target.value)} className={inputCls + " w-20"} />}
                {!isDrill && <button onClick={() => rmSection(i)} className="w-11 h-11 shrink-0 grid place-items-center rounded-xl border-2 border-su-border text-su-danger"><Trash2 className="w-4 h-4" /></button>}
              </div>
            ))}
            {!isDrill && <Btn variant="ghost" size="sm" data-testid="pkg-add-section" onClick={addSection} disabled={catList.length === 0}><Plus className="w-4 h-4" /> Tambah Bagian</Btn>}
            {catList.length === 0 && <p className="text-xs text-su-danger">Belum ada kategori soal untuk {f.exam}.</p>}
          </div>
        </div>

        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" data-testid="pkgf-active" checked={f.active} onChange={(e) => set("active", e.target.checked)} className="w-5 h-5 accent-[var(--su-primary)]" />
          <span className="font-heading font-bold text-sm">Aktif (tampil di halaman Tryout)</span>
        </label>
        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" data-testid="pkgf-mega-only" checked={!!f.mega_only} onChange={(e) => set("mega_only", e.target.checked)} className="w-5 h-5 mt-0.5 accent-[var(--su-primary)] shrink-0" />
          <span className="font-heading font-bold text-sm">Khusus Tryout Akbar<br /><span className="font-medium text-su-muted text-xs">Sembunyikan dari halaman Tryout siswa. Paket tetap bisa dipilih untuk event Akbar dan hanya bisa dikerjakan lewat halaman Akbar.</span></span>
        </label>
      </div>
      <div className="flex gap-3 mt-6">
        <Btn data-testid="pkgf-save" onClick={save} disabled={busy} className="flex-1"><Save className="w-4 h-4" /> {busy ? "Menyimpan..." : "Simpan"}</Btn>
        <Btn variant="ghost" onClick={onCancel}>Batal</Btn>
      </div>
    </div>
  );
}
